import React from "react";
import {
  Button,
  Calendar,
  Icon,
  Input,
  InternationalPhoneInput,
  SelectMenu,
  Text,
} from "@zorni/components-lib";
import { useTranslation } from "react-i18next";
import styles from "../PublishTrip.module.css";
import {
  managerPhoneErrorList,
  managerPhoneFromInputValue,
  managerPhoneToInputValue,
} from "../utils/publishTrip.helpers";

const TripDateFormSection = ({
  editingIndex,
  form,
  stepOptions,
  duration,
  currentInstanceLabel,
  canDeleteCurrent,
  onFormFieldChange,
  onAddStepRow,
  onUpdateStepRow,
  onDeleteStepRow,
  onAddManagerRow,
  onUpdateManagerRow,
  onDeleteManagerRow,
  onDeleteCurrent,
  onSubmit,
}) => {
  const toDateSafe = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const toLocalYyyyMmDd = (value) => {
    if (!(value instanceof Date)) return "";
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const handleRangeChange = (range) => {
    const [start, end] = Array.isArray(range) ? range : [null, null];
    onFormFieldChange("startDate", toLocalYyyyMmDd(start));
    onFormFieldChange("endDate", toLocalYyyyMmDd(end));
  };
  const hasTripPeriod = Boolean(form.startDate && form.endDate);
  const stepMinDate = toDateSafe(form.startDate);
  const stepMaxDate = toDateSafe(form.endDate);

  const handleStepDateChange = (rowIndex, value) => {
    const selectedDate = Array.isArray(value) ? value[0] : value;
    onUpdateStepRow(rowIndex, "date", toLocalYyyyMmDd(selectedDate));
  };
  const { t } = useTranslation();

  return (
    <div className={styles.section}>

      <div className={styles.tripDateCard}>
        <div className={styles.dateHeader}>
          <div>
            <Text as="p" weight="bold">
              {currentInstanceLabel}
            </Text>
            <Text as="p" className={styles.headerHint}>
              {t("tripPublication.publishTrip.instanceConfig")}
            </Text>
          </div>
          <div className={styles.dateActions}>
            {canDeleteCurrent && (
              <Icon
                name="iconTrash"
                size="sm"
                onClick={onDeleteCurrent}
                title={t("tripPublication.publishTrip.actions.deleteInstance")}
              />
            )}
          </div>
        </div>
        <div className={styles.tripDateBody}>

          <div className={styles.priceTag}>
            <Text as="p" className={styles.blockTitle} weight="bold">
              {t("tripPublication.publishTrip.sections.pricing")}
            </Text>
            <div className={styles.grid3}>
              <div className={styles.formGroup}>
                <Input
                  floatingLabel
                  inputSize="medium"
                  label={t("tripPublication.publishTrip.fields.adultPrice")}
                  name="adultPrice"
                  type="number"
                  min="0"
                  value={form.adultPrice}
                  onChange={(event) => onFormFieldChange("adultPrice", event.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <Input
                  floatingLabel
                  inputSize="medium"
                  label={t("tripPublication.publishTrip.fields.childPrice")}
                  name="childPrice"
                  type="number"
                  min="0"
                  value={form.childPrice}
                  onChange={(event) => onFormFieldChange("childPrice", event.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <Input
                  floatingLabel
                  inputSize="medium"
                  label={t("tripPublication.publishTrip.fields.babyPrice")}
                  name="babyPrice"
                  type="number"
                  min="0"
                  value={form.babyPrice}
                  onChange={(event) => onFormFieldChange("babyPrice", event.target.value)}
                />
              </div>
            </div>
          </div>

      <div className={styles.grid3}>
        <div className={`${styles.formGroup} ${styles.span2}`}>
          <Calendar
            type="voyage"
            value={[toDateSafe(form.startDate), toDateSafe(form.endDate)]}
            onChange={handleRangeChange}
            inputLabel={t("tripPublication.publishTrip.fields.period")}
            monthsShownVoyage={2}
            className={styles.calendarRange}
          />
        </div>
        <div className={styles.formGroup}>
          <Input
            floatingLabel
            inputSize="medium"
            label={t("tripPublication.publishTrip.fields.duration")}
            name="duration"
            type="text"
            value={`${duration.nbDays} jour(s) / ${duration.nbNight} nuit(s)`}
            disabled
          />
        </div>
      </div>

      <div className={styles.grid3}>
        <div className={styles.formGroup}>
          <Input
            floatingLabel
            inputSize="medium"
            label={t("tripPublication.publishTrip.fields.adultSeats")}
            name="adultSeats"
            type="number"
            min="0"
            value={form.adultSeats}
            onChange={(event) => onFormFieldChange("adultSeats", event.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <Input
            floatingLabel
            inputSize="medium"
            label={t("tripPublication.publishTrip.fields.childSeats")}
            name="childSeats"
            type="number"
            min="0"
            value={form.childSeats}
            onChange={(event) => onFormFieldChange("childSeats", event.target.value)}
          />
        </div>
        <div className={styles.formGroup}>
          <Input
            floatingLabel
            inputSize="medium"
            label={t("tripPublication.publishTrip.fields.babySeats")}
            name="babySeats"
            type="number"
            min="0"
            value={form.babySeats}
            onChange={(event) => onFormFieldChange("babySeats", event.target.value)}
          />
        </div>
      </div>

          <div className={styles.managersBlock}>
            <div className={styles.stepsHeader}>
              <Text as="p" className={styles.blockTitle} weight="bold">
                {t("tripPublication.publishTrip.sections.travelManagers")}
              </Text>
              <Button
                label={t("tripPublication.publishTrip.actions.addManager")}
                variant="secondary"
                size="small"
                iconName="iconPlus"
                iconPosition="left"
                onClick={onAddManagerRow}
              />
            </div>

            {(form.travelManagers || []).length === 0 && (
              <Text as="p" className={styles.emptyHint}>
                {t("tripPublication.publishTrip.empty.noManager")}
              </Text>
            )}

            {(form.travelManagers || []).map((manager, managerIndex) => (
              <div key={`manager-${managerIndex}`} className={styles.managerRow}>
                <div className={styles.formGroup}>
                  <Input
                    floatingLabel
                    inputSize="medium"
                    label={t("tripPublication.publishTrip.fields.managerFullName")}
                    name={`managerFullName-${managerIndex}`}
                    type="text"
                    value={manager?.fullName || ""}
                    onChange={(event) =>
                      onUpdateManagerRow(managerIndex, "fullName", event.target.value)
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <Input
                    floatingLabel
                    inputSize="medium"
                    label={t("tripPublication.publishTrip.fields.managerEmail")}
                    name={`managerEmail-${managerIndex}`}
                    type="email"
                    value={manager?.email || ""}
                    onChange={(event) =>
                      onUpdateManagerRow(managerIndex, "email", event.target.value)
                    }
                  />
                </div>
                <div className={styles.formGroup}>
                  <InternationalPhoneInput
                    name={`managerPhone-${managerIndex}`}
                    placeholder={t("tripPublication.publishTrip.fields.managerPhone")}
                    value={managerPhoneToInputValue(manager?.phoneNumber)}
                    onChange={(value) =>
                      onUpdateManagerRow(
                        managerIndex,
                        "phoneNumber",
                        managerPhoneFromInputValue(value)
                      )
                    }
                    inputSize="medium"
                    defaultCountry="MA"
                    validate={false}
                    errors={managerPhoneErrorList(manager?.phoneNumber, t)}
                  />
                </div>
                <Icon
                  name="iconTrash"
                  size="sm"
                  onClick={() => onDeleteManagerRow(managerIndex)}
                  title={t("tripPublication.publishTrip.actions.deleteManager")}
                />
              </div>
            ))}
          </div>

          <div className={styles.stepsBlock}>
            <div className={styles.stepsHeader}>
              <Text as="p" className={styles.blockTitle} weight="bold">
                {t("tripPublication.publishTrip.sections.stepsByDay")}
              </Text>
              <Button
                label={t("tripPublication.publishTrip.actions.addStepDate")}
                variant="secondary"
                size="small"
                iconName="iconPlus"
                iconPosition="left"
                onClick={hasTripPeriod ? onAddStepRow : undefined}
                disabled={!hasTripPeriod}
              />
            </div>

            {!hasTripPeriod && (
              <Text as="p" className={styles.emptyHint}>
                {t("tripPublication.publishTrip.toasts.requiredDates")}
              </Text>
            )}

            {form.stepsRows.length === 0 && (
              <Text as="p" className={styles.emptyHint}>
                {t("tripPublication.publishTrip.empty.noStepAssociation")}
              </Text>
            )}

            {form.stepsRows.map((row, rowIndex) => (
              <div key={`${rowIndex}-${row.stepId}-${row.date}`} className={styles.stepRow}>
                <div className={styles.formGroup}>
                  <Calendar
                    type="voyage"
                    selectionMode="single"
                    value={toDateSafe(row.date)}
                    minDate={stepMinDate}
                    maxDate={stepMaxDate}
                    onChange={(value) => handleStepDateChange(rowIndex, value)}
                    inputLabel={t("tripPublication.publishTrip.fields.date")}
                  />
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.selectMenuLayer}>
                    <SelectMenu t={t}
                      name={`stepId-${rowIndex}`}
                      activeSearch={true}
                      options={stepOptions.map((step, index) => ({
                        index,
                        value: step.id,
                        text: step?.ranking?.title || step.id,
                        filterText: step?.ranking?.title || step.id,
                      }))}
                      placeholder={t("tripPublication.publishTrip.placeholders.selectStep")}
                      value={row.stepId || ""}
                      onChange={(_, option) =>
                        onUpdateStepRow(rowIndex, "stepId", option?.value || "")
                      }
                    />
                  </div>
                </div>

                <Icon
                  name="iconTrash"
                  size="sm"
                  onClick={() => onDeleteStepRow(rowIndex)}
                  title={t("tripPublication.publishTrip.actions.deleteStep")}
                />
              </div>
            ))}
          </div>

          <div className={styles.formActions}>
            <Button
              primary={true}
              modeOfUse="edit"
              size="small"
              label={
                editingIndex === null
                  ? t("tripPublication.publishTrip.actions.addDeparture")
                  : t("tripPublication.publishTrip.actions.saveChanges")
              }
              iconName={editingIndex === null ? "iconPlus" : undefined}
              iconPosition={editingIndex === null ? "left" : undefined}
              onClick={onSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TripDateFormSection;
